import { Injectable, inject } from '@angular/core'
import type { ActivatedRouteSnapshot, RouterStateSnapshot, Route } from '@angular/router'
import { Router } from '@angular/router'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { Observable } from 'rxjs'
import { throwError, catchError, map, of, switchMap } from 'rxjs'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { WorkspaceNavigationService } from '../framework/services/workspace-navigation-service/workspace-navigation.service'

const TOKEN_EXPIRY_SKEW_MS = 60_000

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private router = inject(Router)
  private workspaceNavigationService = inject(WorkspaceNavigationService)
  private accountQuery = inject(AccountQuery)
  private accountService = inject(AccountService)
  private browserService = inject(BrowserService)
  private logService = inject(LogService)
  private signalStoreService = inject(SignalStoreService)

  refreshToken: string
  accessToken: string
  subdomain: string
  email: string
  expiresIn: number
  redirect: URL

  currentUrl: string
  private isReady: boolean

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const url = state.url

    this.currentUrl = url

    // Determinate if the route comes from AuthComponent
    const fromAuth = route.parent && route.parent.data.module === 'auth'
    const guardKeep = route.data.guardKeep === true

    return this.checkLogin(guardKeep, fromAuth)
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(route, state)
  }

  canLoad(route: Route): Observable<boolean> {
    return this.checkLogin()
  }

  /**
   * Checks if the user is logged in and redirect if needed
   * @param {boolean} [guardKeep=false] True to prevent redirection
   * @param {boolean} [fromAuth=false] If is from the Auth module or not
   * @returns {Observable<boolean>} If can navigate
   */
  checkLogin(guardKeep = false, fromAuth = false): Observable<boolean> {
    // Wait until the device storage is ready
    return this.signalStoreService.selectOnStoreRestored('account').pipe(
      switchMap(() => {
        this.isReady = true

        const canNavigate = this.checkLoginLogic(guardKeep, fromAuth)
        const account = this.accountQuery.getValue()
        const shouldRefresh =
          canNavigate &&
          !fromAuth &&
          !!account.email &&
          !!account.refreshAt &&
          new Date(account.refreshAt).getTime() <= Date.now() + TOKEN_EXPIRY_SKEW_MS

        if (!shouldRefresh) {
          return of(canNavigate)
        }

        // Refresh errors fail the navigation so startup recovery applies. A rejected session starts a logout,
        // whose onboarding navigation replaces the error only if the logout request succeeds.
        return this.accountService.refreshSession(false).pipe(map(() => true))
      }),
      catchError(error => {
        console.log('Error in AuthGuard.checkLoginLogic()', error)
        this.logService.logMessage(Level.error, 'Error in AuthGuard.checkLoginLogic(): ' + error?.message, { error })

        return throwError(error)
      })
    )
  }

  /**
   * Checks if the user should be forwarded to the framework or not
   * @param {boolean} [guardKeep=false]
   * @param {boolean} [fromAuth=false]
   * @returns {boolean}
   */
  private checkLoginLogic(guardKeep = false, fromAuth = false): boolean {
    const account = this.accountQuery.getValue()
    const isLoggedIn = !!account.email && !!account.refreshAt

    if (isLoggedIn && (!fromAuth || guardKeep)) {
      // If is logged in and is not from Auth module or guardKeep flag is true
      return true
    } else if (isLoggedIn && fromAuth) {
      // If is logged in and is from Auth module
      // Navigate to the dashboard page if already logged in
      void this.workspaceNavigationService.open()

      return false
    } else if (!isLoggedIn && fromAuth) {
      // If is not logged in and is from Auth module
      return true
    }

    // Navigate to the login page if not logged in and not from Auth module
    this.router.navigate(['/onboarding'])

    return false
  }
}
